// 兼职人员树公共组件（可复用）：TDesign Tree 树形勾选 + 顶部全选 + 勾选后节点右侧显示数量输入框（普通数字输入框，无加减号）
// props: tree(兼职树数组), value({userId:count}), onChange(更新)
// 均分按钮由外层弹窗放在「兼职人员」标题后统一提供；保存统一由外层弹窗右下角【保存】触发。
import React, { useMemo } from 'react';
import { Tree, Input, Checkbox } from 'tdesign-react';

const ROLE_LABEL = { LEADER: '团长', PERSON: '个人', LEADER_SUB: '团长下个人' };

// 兼职树 → TDesign Tree data（label 拼接名称与角色）
const mapTree = (nodes) => (nodes || []).map(n => ({
  label: `${n.name}${n.role ? `（${ROLE_LABEL[n.role] || n.role}）` : ''}`,
  value: n.userId,
  children: n.children && n.children.length ? mapTree(n.children) : undefined,
}));

// 收集全部可分配节点 userId（含团长：团长本身也参与数量分配与均分）
const collectAll = (nodes) => (nodes || []).reduce((acc, n) => {
  acc.push(n.userId);
  if (n.children && n.children.length) return acc.concat(collectAll(n.children));
  return acc;
}, []);

/**
 * 兼职人员树：TDesign Tree 勾选（级联：勾选团长自动勾选其下个人）；勾选节点右侧显示数量输入框。
 */
export default function WorkerTree({ tree = [], value = {}, onChange }) {
  const treeData = useMemo(() => mapTree(tree), [tree]);
  // 全部可分配人员（含团长）与已勾选数量：用于全选勾选框的状态
  const allIds = useMemo(() => collectAll(tree), [tree]);
  const checkedCount = allIds.filter(id => value[id] > 0).length;
  const allChecked = allIds.length > 0 && checkedCount === allIds.length;
  const halfChecked = checkedCount > 0 && !allChecked;
  // 已勾选节点（count>0 视为勾选）；valueMode=all，勾选团长时其本人与团下个人都会出现在选中值中
  const checkedKeys = Object.keys(value).filter(k => value[k] > 0);

  // Tree 勾选变化：保留原数量，新勾选节点默认 1，取消勾选的置 0
  const onCheck = (vals) => {
    const sel = new Set((vals || []).map(String));
    const next = { ...value };
    Object.keys(next).forEach(k => { if (!sel.has(k)) next[k] = 0; });
    sel.forEach(k => { if (!(next[k] > 0)) next[k] = 1; });
    onChange(next);
  };

  // 全选/取消全选：勾选全部可分配人员（含团长；原数量保留，未勾选默认 1），取消则全部置 0
  const toggleAll = (on) => {
    const next = { ...value };
    allIds.forEach(id => { next[id] = on ? Math.max(next[id] || 0, 1) : 0; });
    onChange(next);
  };

  // 自定义节点内容：名称 + 勾选后显示数量输入框（普通输入框，阻止点击冒泡以免误触发树节点事件）
  const renderLabel = (node) => {
    const uid = String(node.value);
    const isChecked = value[uid] > 0;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        <span>{node.label}</span>
        {isChecked && (
          <Input
            type="number"
            value={String(value[uid] || 0)}
            onClick={(ctx) => ctx.e && ctx.e.stopPropagation()}
            onChange={(v) => onChange({ ...value, [uid]: Number(v || 0) })}
            style={{ width: 100, marginLeft: 10 }}
          />
        )}
      </span>
    );
  };

  return (
    <div>
      {tree.length === 0
        ? <span style={{ color: '#999' }}>暂无已审核通过的兼职</span>
        : (
          <div>
            {/* 全选勾选框：一键勾选/取消全部可分配人员（含团长） */}
            <div style={{ marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #f0f0f0' }}>
              <Checkbox checked={allChecked} indeterminate={halfChecked} onChange={toggleAll}>
                <span>全选（共 {allIds.length} 人）</span>
              </Checkbox>
            </div>
            <Tree
              data={treeData}
              checkable
              value={checkedKeys}
              valueMode="all"
              onChange={onCheck}
              label={renderLabel}
              expandAll
            />
          </div>
        )}
    </div>
  );
}
